import { ICustomReport, ReportFilterComparison } from '@principle-theorem/principle-core/interfaces';
import { DocumentReference, ITimePeriod, isDocRef, isTimePeriod, TypeGuard } from '@principle-theorem/shared';
import { isString } from 'lodash';
import * as moment from 'moment-timezone';
import { IQueryScope, isQueryScope } from './scoped-query';

export enum GroupBy {
  Ref = 'ref.referenceValue',
  Gap = 'gap.ref.referenceValue',
  Task = 'task.ref.referenceValue',
  LabJob = 'labJob.ref.referenceValue',
  Invoice = 'invoice.ref.referenceValue',
  Patient = 'patient.ref.referenceValue',
  Referral = 'referrer.ref.referenceValue',
  Practice = 'practice.ref.referenceValue',
  Treatment = 'treatment.ref.referenceValue',
  PatientAge = 'patient.age',
  Appointment = 'appointment.ref.referenceValue',
  Interaction = 'interaction.ref.referenceValue',
  PaymentPlan = 'paymentPlan.ref.referenceValue',
  Practitioner = 'practitioner.ref.referenceValue',
  PatientGender = 'patient.gender',
  TreatmentPlan = 'treatmentPlan.ref.referenceValue',
  TreatmentStep = 'treatmentStepRef.referenceValue',
  PatientDistance = 'patient.distance',
  Transaction = 'transaction.ref.referenceValue',
  AccountCredit = 'accountCredit.ref.referenceValue',
  ScheduleSummary = 'scheduleSummaryRef.referenceValue',
  SchedulingEvent = 'schedulingEventRef.referenceValue',
}

export enum WhereOperation {
  Or = 'OR',
  And = 'AND',
  Between = 'BETWEEN',
}

export enum SortOrder {
  Ascending = 'ASC',
  Descending = 'DESC',
}

export interface IOrderBy {
  attribute: string;
  sortOrder: SortOrder;
}

export type QueryFilter = string;

export interface IUnnestQuery {
  property: string;
  alias: string;
}

export const isUnnestQuery = TypeGuard.interface<IUnnestQuery>({
  property: isString,
  alias: isString,
});

export interface ITableJoin {
  table: string;
  alias: string;
  sourceJoinKey: string;
  destinationJoinKey: string;
  joinType: 'LEFT' | 'INNER';
  orderByProperty?: string;
}

export const isTableJoin = TypeGuard.interface<ITableJoin>({
  table: isString,
  alias: isString,
  sourceJoinKey: isString,
  destinationJoinKey: isString,
  joinType: isString,
  orderByProperty: TypeGuard.undefinedOr(isString),
});

export interface IScopedQuery {
  dataSet: string;
  query: IReportingQuery;
}

export interface IReportingQueryRequest {
  scope: IQueryScope;
  query: IReportingQuery;
  customReportRef?: DocumentReference<ICustomReport>;
  filterDate?: moment.Moment;
  filterDateRange?: ITimePeriod;
}

export const isReportingQueryRequest =
  TypeGuard.interface<IReportingQueryRequest>({
    scope: isQueryScope,
    query: isReportingQuery,
    customReportRef: TypeGuard.undefinedOr(isDocRef),
    filterDate: TypeGuard.undefinedOr(moment.isMoment),
    filterDateRange: TypeGuard.undefinedOr(isTimePeriod),
  });

// Escape hatch for queries too complex for ITableJoin (e.g. inequality joins, window functions)
export interface IRawCte {
  name: string;
  sql: string;
}

export const isRawCte = TypeGuard.interface<IRawCte>({
  name: isString,
  sql: isString,
});

export interface IComputedAttribute {
  expression: string;
  alias: string;
}

export const isComputedAttribute = TypeGuard.interface<IComputedAttribute>({
  expression: isString,
  alias: isString,
});

export interface IReportingQuery {
  table: string;
  attributes: string[];
  filters?: QueryFilter[];
  joins?: ITableJoin[];
  groupBy?: GroupBy[];
  orderBy?: IOrderBy[];
  subQuery?: IReportingQuery;
  unnest?: IUnnestQuery;
  computedAttributes?: IComputedAttribute[];
  rawCtes?: IRawCte[];
}

export function isReportingQuery(item: unknown): item is IReportingQuery {
  return TypeGuard.interface<IReportingQuery>({
    table: isString,
    attributes: TypeGuard.arrayOf(isString),
    filters: TypeGuard.undefinedOr(TypeGuard.arrayOf(isString)),
    joins: TypeGuard.undefinedOr(TypeGuard.arrayOf(isTableJoin)),
    groupBy: TypeGuard.undefinedOr(TypeGuard.arrayOf(isString)),
    orderBy: TypeGuard.undefinedOr(TypeGuard.arrayOf(isString)),
    subQuery: TypeGuard.undefinedOr(isReportingQuery),
    unnest: TypeGuard.undefinedOr(isUnnestQuery),
    computedAttributes: TypeGuard.undefinedOr(
      TypeGuard.arrayOf(isComputedAttribute)
    ),
    rawCtes: TypeGuard.undefinedOr(TypeGuard.arrayOf(isRawCte)),
  })(item);
}

export type BigQueryComparison =
  | '='
  | '!='
  | '>'
  | '>='
  | '<'
  | '<='
  | 'IS'
  | 'IS NOT';

export type BigQuerySpecialComparison =
  | 'NORMALIZE_AND_CASEFOLD' // string equals
  | 'CONTAINS_SUBSTR' // contains
  | 'NOT CONTAINS_SUBSTR' // does not contain
  | 'STARTS_WITH' // starts with
  | 'ENDS_WITH'; // ends with

export const REPORT_FILTER_BIG_QUERY_COMPARISON_MAP: {
  [key in ReportFilterComparison]:
    | BigQueryComparison
    | BigQuerySpecialComparison;
} = {
  // TODO: Implement these comparisons
  // [ReportFilterComparison.IsEmpty]: 'IS',
  // [ReportFilterComparison.IsNotEmpty]: 'IS NOT',

  // Text Comparisons
  [ReportFilterComparison.TextIsEqualTo]: 'NORMALIZE_AND_CASEFOLD',
  [ReportFilterComparison.TextContains]: 'CONTAINS_SUBSTR',
  [ReportFilterComparison.TextDoesNotContain]: 'NOT CONTAINS_SUBSTR',
  [ReportFilterComparison.TextStartsWith]: 'STARTS_WITH',
  [ReportFilterComparison.TextEndsWith]: 'ENDS_WITH',
  [ReportFilterComparison.TextIsExactly]: '=',

  // Date Comparisons
  [ReportFilterComparison.DateIs]: '=',
  [ReportFilterComparison.DateIsBefore]: '<=',
  [ReportFilterComparison.DateIsAfter]: '>=',
  [ReportFilterComparison.DateIsBetween]: '>=', // This is a special case, handled in the dateRange function

  // Relative Date Comparisons
  [ReportFilterComparison.DateIsAtLeast]: '>=', // Depending on relative direction this could be '<=',
  [ReportFilterComparison.DateIsAtMost]: '<=', // Depending on relative direction this could be '>=',

  // Numeric Comparisons
  [ReportFilterComparison.GreaterThan]: '>',
  [ReportFilterComparison.GreaterThanOrEqualTo]: '>=',
  [ReportFilterComparison.LessThan]: '<',
  [ReportFilterComparison.LessThanOrEqualTo]: '<=',
  [ReportFilterComparison.IsEqualTo]: '=',
  [ReportFilterComparison.IsNotEqualTo]: '!=',
};
